import { useState } from 'react';
import { useGT } from 'gt-react';
import {
  useCreateForecastFactor,
  useUpdateForecastFactor,
  type ForecastFactorRow,
} from 'src/api/rpc/hooks/procurement/useForecastFactors';
import {
  beginPolicyOverrideEdit,
  policyOverrideMutationHandlers,
} from '../components/policyOverrideMutationFeedback';
import {
  useOverrideWindowState,
  type OverrideWindowState,
} from '../components/useOverrideWindowState';
import {
  factorInRange,
  factorToPercent,
  parsePercent,
  percentToFactor,
} from './forecastFactorRows';

export interface ForecastFactorFormTarget {
  warehouseId: string;
  productV3Key: string;
}

export interface ForecastFactorFormState {
  percent: string;
  setPercent: (value: string) => void;
  /** Ends / review-by / reason, the same window the other override forms edit. */
  window: OverrideWindowState;
  /** The row being replaced, or null when adding a new factor. */
  editing: ForecastFactorRow | null;
  startEdit: (o: ForecastFactorRow) => void;
  reset: () => void;
  submit: () => void;
  /** The multiplier the percent resolves to; null while the field is empty or not a number. */
  factorValue: number | null;
  factorValid: boolean;
  canSubmit: boolean;
  pending: boolean;
  target: ForecastFactorFormTarget;
}

export function useForecastFactorForm(
  target: ForecastFactorFormTarget,
): ForecastFactorFormState {
  const gt = useGT();
  const createMutation = useCreateForecastFactor();
  const updateMutation = useUpdateForecastFactor();
  const windowState = useOverrideWindowState('month');

  const [percent, setPercent] = useState('');
  const [editing, setEditing] = useState<ForecastFactorRow | null>(null);

  const pct = parsePercent(percent);
  const factorValue = pct === null ? null : percentToFactor(pct);
  const factorValid = factorValue !== null && factorInRange(factorValue);
  const pending = createMutation.isPending || updateMutation.isPending;
  const canSubmit =
    factorValid && windowState.isComplete && windowState.orderValid && !pending;

  function reset() {
    setEditing(null);
    setPercent('');
    windowState.reset();
  }

  const { onSaved, onError } = policyOverrideMutationHandlers(reset, (e) =>
    gt('Failed to save growth factor: {msg}', {
      context: 'growth factor error toast',
      msg: e.message,
    }),
  );

  function startEdit(o: ForecastFactorRow) {
    beginPolicyOverrideEdit(
      o,
      setEditing,
      setPercent,
      String(factorToPercent(o.factor)),
      windowState,
    );
  }

  function submit() {
    if (!canSubmit || factorValue === null) return;
    const payload = { factor: factorValue, ...windowState.payload };
    if (editing) {
      updateMutation.mutate(
        { overrideId: editing.id, ...payload },
        {
          onSuccess: () =>
            onSaved(
              gt('Growth factor updated', { context: 'growth factor toast' }),
            ),
          onError,
        },
      );
      return;
    }
    createMutation.mutate(
      { ...target, ...payload },
      {
        onSuccess: () =>
          onSaved(
            gt('Growth factor created', { context: 'growth factor toast' }),
          ),
        onError,
      },
    );
  }

  return {
    percent,
    setPercent,
    window: windowState,
    editing,
    startEdit,
    reset,
    submit,
    factorValue,
    factorValid,
    canSubmit,
    pending,
    target,
  };
}
